import {QuoteHistoryData} from '../../Core/mobx/ParameterStore/types';
import {TemplateDataFields} from '../ContactForm/EmailEditor/EmailEditor';
import {PolicyInfoProductTypes, TemplateAfterSaleQuoteData} from '../types';

export const useCombinationQuote = (singleQuote: QuoteHistoryData | null, combinedQuote: QuoteHistoryData | null) => {
    const lastQuote = singleQuote;
    const lastCombinationQuote = combinedQuote;

    const termPeriod =
        +lastQuote?.out?.find((o: any) => o.name === 'Product type')?.value?.split(' ')[0] ||
        +lastCombinationQuote?.out?.find((o: any) => o.name === 'Product type')?.value?.split(' ')[0];
    // @ts-ignore
    const faceAmountQuote = +lastQuote?.faceAmount || 0;
    // @ts-ignore
    const faceAmountQuoteCombination = +lastCombinationQuote?.faceAmount || 0;
    const InitialDB =
        +lastQuote?.out?.find((o: any) => o.name === 'Initial death benefit')?.value ||
        +lastCombinationQuote?.out?.find((o: any) => o.name === 'Initial death benefit')?.value ||
        0;
    // @ts-ignore
    const premiumMonthlyQuote = +lastQuote?.monthlyPremium || 0;
    // @ts-ignore
    const premiumMonthlyQuoteCombination = +lastCombinationQuote?.monthlyPremium || 0;
    let initialAnnual =
        +lastQuote?.out?.find((o: any) => o.name === 'Initial annual premium')?.value ||
        +lastCombinationQuote?.out?.find((o: any) => o.name === 'Initial annual premium')?.value ||
        0;
    // check if product type is LSWFlexLife or LSWFlexLifeNL then set initialAnnual with Target premium data
    if (lastQuote?.productName === 'FlexLife' || lastQuote?.productName === 'FlexLife NL') {
        initialAnnual = Number(lastQuote?.out?.find((o: any) => o.name === 'Target premium')?.value);
    } else if (
        lastCombinationQuote?.productName === 'FlexLife' ||
        lastCombinationQuote?.productName === 'FlexLife NL'
    ) {
        initialAnnual = Number(lastCombinationQuote?.out?.find((o: any) => o.name === 'Target premium')?.value);
    }

    const singleQuoteProductType = singleQuote?.productType;
    const combinedQuoteProductType = combinedQuote?.productType;

    let cashValue =
        +lastQuote?.out?.find((o: any) => o.fieldName === TemplateDataFields.cashValue)?.value ||
        +lastCombinationQuote?.out?.find((o: any) => o.fieldName === TemplateDataFields.cashValue)?.value ||
        0;
    const prosperCashValueAtAge70 = +lastQuote?.out?.find((o: any) => o.name === 'Cash Value at Age 70')?.value;
    cashValue = prosperCashValueAtAge70 || cashValue;

    const checkCombination = (first: string, second: string) => {
        return (
            (singleQuoteProductType === first && combinedQuoteProductType === second) ||
            (singleQuoteProductType === second && combinedQuoteProductType === first)
        );
    };
    const first = checkCombination('Term', 'IUL');
    const second = checkCombination('Term', 'GUL');
    const third = checkCombination('IUL', 'GUL');
    const fourth = checkCombination('LSWIUL', 'Term');

    const addTwoAmounts = (firstNumber: number, secondNumber: number) => {
        return (firstNumber * 100 + secondNumber * 100) / 100;
    };

    let coverage = faceAmountQuote;
    let monthlyPremium = premiumMonthlyQuote;
    let lifePremium = initialAnnual;
    let lifeCoverage = InitialDB;

    if (first || fourth) {
        coverage = faceAmountQuoteCombination + faceAmountQuote;
        lifeCoverage = InitialDB;
        monthlyPremium = addTwoAmounts(premiumMonthlyQuote, premiumMonthlyQuoteCombination);
        lifePremium = initialAnnual / 12;
    }

    if (second) {
        coverage = faceAmountQuoteCombination + faceAmountQuote;
        lifeCoverage = singleQuoteProductType === 'GUL' ? faceAmountQuote : faceAmountQuoteCombination;
        monthlyPremium = premiumMonthlyQuote + premiumMonthlyQuoteCombination;
        lifePremium = combinedQuoteProductType === 'GUL' ? premiumMonthlyQuoteCombination : premiumMonthlyQuote;
    }

    if (third) {
        coverage = InitialDB + faceAmountQuoteCombination;
        lifePremium = initialAnnual / 12 + premiumMonthlyQuoteCombination;
    }

    return [
        {
            termPeriod,
            coverage,
            monthlyPremium,
            lifePremium,
            lifeCoverage,
            cashValue,
            singleQuoteProductType,
            combinedQuoteProductType,
        },
    ];
};

export const useAfterSaleCombinationQuote = (
    firstQuote: TemplateAfterSaleQuoteData,
    secondQuote?: TemplateAfterSaleQuoteData,
) => {
    const productTypes = [firstQuote.productType, secondQuote?.productType];
    let termPeriod = 0;
    if (productTypes.includes(PolicyInfoProductTypes.TERM_10)) termPeriod = 10;
    if (productTypes.includes(PolicyInfoProductTypes.TERM_15)) termPeriod = 15;
    if (productTypes.includes(PolicyInfoProductTypes.TERM_20)) termPeriod = 20;
    if (productTypes.includes(PolicyInfoProductTypes.TERM_25)) termPeriod = 25;
    if (productTypes.includes(PolicyInfoProductTypes.TERM_30)) termPeriod = 30;
    if (productTypes.includes(PolicyInfoProductTypes.TERM_35)) termPeriod = 35;
    if (productTypes.includes(PolicyInfoProductTypes.TERM_40)) termPeriod = 40;

    let {
        coverageAmount: coverage,
        coverageAmount: lifeCoverage,
        monthlyPremium,
        monthlyPremium: lifePremium,
    } = firstQuote;

    if (secondQuote && secondQuote.productType) {
        const hasTerm = productTypes.some((v) =>
            [
                PolicyInfoProductTypes.TERM_10,
                PolicyInfoProductTypes.TERM_15,
                PolicyInfoProductTypes.TERM_20,
                PolicyInfoProductTypes.TERM_25,
                PolicyInfoProductTypes.TERM_30,
            ].includes(v!),
        );
        coverage = +firstQuote.coverageAmount + +secondQuote.coverageAmount;
        if (productTypes.includes(PolicyInfoProductTypes.IUL) && productTypes.includes(PolicyInfoProductTypes.GUL)) {
            lifePremium = +firstQuote.monthlyPremium + +secondQuote.monthlyPremium;
        } else if (productTypes.includes(PolicyInfoProductTypes.IUL) && hasTerm) {
            monthlyPremium = +firstQuote.monthlyPremium + +secondQuote.monthlyPremium;
            lifePremium =
                firstQuote.productType === PolicyInfoProductTypes.IUL
                    ? +firstQuote.monthlyPremium
                    : +secondQuote.monthlyPremium;
            lifeCoverage =
                firstQuote.productType === PolicyInfoProductTypes.IUL
                    ? +firstQuote.coverageAmount
                    : +secondQuote.coverageAmount;
        } else if (productTypes.includes(PolicyInfoProductTypes.GUL) && hasTerm) {
            monthlyPremium = +firstQuote.monthlyPremium + +secondQuote.monthlyPremium;
            lifePremium =
                firstQuote.productType === PolicyInfoProductTypes.GUL
                    ? +firstQuote.monthlyPremium
                    : +secondQuote.monthlyPremium;
            lifeCoverage =
                firstQuote.productType === PolicyInfoProductTypes.GUL
                    ? +firstQuote.coverageAmount
                    : +secondQuote.coverageAmount;
        }
    }
    return [
        {
            termPeriod,
            coverage,
            monthlyPremium,
            lifePremium,
            lifeCoverage,
        },
    ];
};
